import { PublicClientApplication, InteractionStatus } from '@azure/msal-browser';

export const LOGIN_SCOPES = ['User.Read'];

export const loginRequest = {
  scopes: LOGIN_SCOPES,
  prompt: 'select_account',
};

export const msalConfig = {
  auth: {
    clientId: process.env.REACT_APP_CLIENT_ID || 'ee4e0498-f78c-409d-ab82-2a1bcc80e47e',
    authority: `https://login.microsoftonline.com/${process.env.REACT_APP_TENANT_ID || 'd205684f-6cde-4d47-93fb-e1b1848048ea'}`,
    redirectUri: process.env.REACT_APP_REDIRECT_URI || window.location.origin,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: true,
  },
};

export const msalInstance = new PublicClientApplication(msalConfig);

let msalBootstrapPromise = null;

/**
 * Mobile browsers mishandle popup opener communication; use redirect flow instead.
 */
export const isMobileDevice = () => {
  if (typeof navigator === 'undefined') {
    return false;
  }
  const ua = navigator.userAgent || '';
  const mobileUa =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
  const coarsePointer =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(pointer: coarse)')?.matches === true;
  return mobileUa || (navigator.maxTouchPoints > 1 && coarsePointer);
};

export const cleanAuthRedirectFromUrl = () => {
  const hasHashAuth =
    window.location.hash.includes('code=') ||
    window.location.hash.includes('error=');
  const hasQueryAuth =
    window.location.search.includes('code=') ||
    window.location.search.includes('error=');
  if (hasHashAuth || hasQueryAuth) {
    window.history.replaceState({}, document.title, window.location.pathname);
  }
};

const ensureActiveAccount = () => {
  const active = msalInstance.getActiveAccount();
  if (active) {
    return active;
  }
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
    msalInstance.setActiveAccount(accounts[0]);
    return accounts[0];
  }
  return null;
};

/**
 * Initialize MSAL and process redirect return (must run once before app render).
 */
export const bootstrapMsal = async () => {
  if (!msalBootstrapPromise) {
    msalBootstrapPromise = (async () => {
      await msalInstance.initialize();
      const redirectResponse = await msalInstance.handleRedirectPromise();
      if (redirectResponse?.account) {
        msalInstance.setActiveAccount(redirectResponse.account);
        cleanAuthRedirectFromUrl();
      } else {
        ensureActiveAccount();
      }
    })();
  }
  return msalBootstrapPromise;
};

export const isAuthInteractionInProgress = (inProgress) =>
  [
    InteractionStatus.Login,
    InteractionStatus.SsoSilent,
    InteractionStatus.HandleRedirect,
    InteractionStatus.AcquireToken,
    InteractionStatus.Startup,
  ].includes(inProgress);

export const loginWithMicrosoft = async (instance) => {
  if (isMobileDevice()) {
    await instance.loginRedirect(loginRequest);
    return null;
  }
  const result = await instance.loginPopup(loginRequest);
  if (result?.account) {
    instance.setActiveAccount(result.account);
  }
  return result;
};

export const logoutWithMicrosoft = async (instance, account) => {
  const logoutRequest = {
    account,
    postLogoutRedirectUri: window.location.origin,
  };
  if (isMobileDevice()) {
    await instance.logoutRedirect(logoutRequest);
    return;
  }
  await instance.logoutPopup({
    ...logoutRequest,
    mainWindowRedirectUri: window.location.origin,
  });
};

export const acquireTokenInteractive = async (tokenRequest) => {
  if (isMobileDevice()) {
    await msalInstance.acquireTokenRedirect(tokenRequest);
    return null;
  }
  return msalInstance.acquireTokenPopup(tokenRequest);
};
